import { useState, useEffect, useCallback, useRef } from 'react';
import { MAP_PROVIDERS } from './useMapProvider';

// Get tile URL template based on current provider
const getTileUrlTemplate = () => {
  const savedProvider = localStorage.getItem('strategos_map_provider') || 'osm';
  const provider = MAP_PROVIDERS[savedProvider] || MAP_PROVIDERS.osm;
  // Replace {s} with 'a' for consistent caching, remove {r} for retina
  return provider.url.replace('{s}', 'a').replace('{r}', '');
};

// Calculate tiles for a bounding box at specific zoom levels
function getTilesForBounds(bounds, minZoom, maxZoom) {
  const tiles = [];
  const { north, south, east, west } = bounds;
  
  for (let zoom = minZoom; zoom <= maxZoom; zoom++) {
    const minTileX = lon2tile(west, zoom);
    const maxTileX = lon2tile(east, zoom);
    const minTileY = lat2tile(north, zoom);
    const maxTileY = lat2tile(south, zoom);
    
    for (let x = minTileX; x <= maxTileX; x++) {
      for (let y = minTileY; y <= maxTileY; y++) {
        tiles.push({ z: zoom, x, y });
      }
    }
  }
  
  return tiles;
}

// Convert longitude to tile X
function lon2tile(lon, zoom) {
  return Math.floor((lon + 180) / 360 * Math.pow(2, zoom));
}

// Convert latitude to tile Y
function lat2tile(lat, zoom) {
  return Math.floor(
    (1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom)
  );
}

// Calculate bounds from array of coordinates
function getBoundsFromCoordinates(coordinates) {
  if (!coordinates || coordinates.length === 0) return null;
  
  let north = -90, south = 90, east = -180, west = 180;
  
  for (const [lat, lon] of coordinates) {
    if (lat > north) north = lat;
    if (lat < south) south = lat;
    if (lon > east) east = lon;
    if (lon < west) west = lon;
  }
  
  // Add padding (about 2km buffer)
  const latPadding = 0.02;
  const lonPadding = 0.02;
  
  return {
    north: north + latPadding,
    south: south - latPadding,
    east: east + lonPadding,
    west: west - lonPadding
  };
}

// Get tile URL template dynamically
const TILE_URL_TEMPLATE = getTileUrlTemplate();

// Format bytes to human readable
const formatSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export function useOfflineMap() {
  const [isSupported, setIsSupported] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ cached: 0, failed: 0, total: 0 });
  const [cacheInfo, setCacheInfo] = useState({ size: 0, count: 0 });
  const [error, setError] = useState(null);
  const [cancelled, setCancelled] = useState(false);
  
  const swRegistrationRef = useRef(null);

  // Get cache info function
  const getCacheInfo = useCallback(() => {
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'GET_CACHE_SIZE' });
    }
  }, []);

  // Handle SW messages
  const handleSWMessage = useCallback((event) => {
    const { type, ...data } = event.data;
    
    switch (type) {
      case 'CACHE_PROGRESS':
        setProgress({ cached: data.cached, failed: data.failed, total: data.total });
        break;
      case 'CACHE_COMPLETE':
        setDownloading(false);
        setCancelled(false);
        setProgress({ cached: data.cached, failed: data.failed, total: data.total });
        // Refresh cache info after download
        setTimeout(() => {
          if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'GET_CACHE_SIZE' });
          }
        }, 500);
        break;
      case 'CACHE_CANCELLED':
        setDownloading(false);
        setCancelled(false);
        setProgress({ cached: data.cached, failed: data.failed, total: data.total });
        // Refresh cache info
        setTimeout(() => {
          if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'GET_CACHE_SIZE' });
          }
        }, 500);
        break;
      case 'CACHE_CLEARED':
        setCacheInfo({ size: 0, count: 0 });
        break;
      case 'CACHE_SIZE':
        setCacheInfo({ size: data.size, count: data.count });
        break;
      default:
        break;
    }
  }, []);

  // Register service worker on mount
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      setIsSupported(true);
      
      // Add message listener
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
      
      // Register SW
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered:', registration);
          swRegistrationRef.current = registration;
          
          // Wait for SW to be ready, then get cache info
          navigator.serviceWorker.ready.then(() => {
            setTimeout(getCacheInfo, 1000);
          });
        })
        .catch((err) => {
          console.error('SW registration failed:', err);
          setError('No se pudo registrar el Service Worker');
        });
        
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      };
    }
  }, [handleSWMessage, getCacheInfo]);

  // Download tiles for a track
  const downloadTrackArea = useCallback(async (coordinates, options = {}) => {
    const { minZoom = 10, maxZoom = 15 } = options;
    
    if (!navigator.serviceWorker?.controller) {
      setError('Service Worker no está listo. Recarga la página.');
      return false;
    }
    
    const bounds = getBoundsFromCoordinates(coordinates);
    if (!bounds) {
      setError('No se pudieron calcular los límites del track');
      return false;
    }
    
    const tiles = getTilesForBounds(bounds, minZoom, maxZoom);
    const tileUrls = tiles.map(t => 
      TILE_URL_TEMPLATE.replace('{z}', t.z).replace('{x}', t.x).replace('{y}', t.y)
    );
    
    console.log(`Downloading ${tileUrls.length} tiles for track area...`);
    
    setDownloading(true);
    setProgress({ cached: 0, failed: 0, total: tileUrls.length });
    setError(null);
    
    navigator.serviceWorker.controller.postMessage({
      type: 'CACHE_TILES',
      tiles: tileUrls
    });
    
    return true;
  }, []);

  // Clear all cached tiles
  const clearCache = useCallback(() => {
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_TILE_CACHE' });
    }
  }, []);

  // Cancel ongoing download
  const cancelDownload = useCallback(() => {
    if (navigator.serviceWorker?.controller && downloading) {
      setCancelled(true);
      navigator.serviceWorker.controller.postMessage({ type: 'CANCEL_DOWNLOAD' });
    }
  }, [downloading]);

  // Estimate download size
  const estimateDownloadSize = useCallback((coordinates, options = {}) => {
    const { minZoom = 10, maxZoom = 15 } = options;
    
    const bounds = getBoundsFromCoordinates(coordinates);
    if (!bounds) return null;
    
    const tiles = getTilesForBounds(bounds, minZoom, maxZoom);
    // Average tile size is ~20KB for OpenTopoMap
    const estimatedBytes = tiles.length * 20 * 1024;
    
    return {
      tileCount: tiles.length,
      estimatedSize: formatSize(estimatedBytes),
      estimatedBytes
    };
  }, []);

  return {
    isSupported,
    downloading,
    cancelled,
    progress,
    cacheInfo: {
      ...cacheInfo,
      formattedSize: formatSize(cacheInfo.size)
    },
    error,
    downloadTrackArea,
    clearCache,
    cancelDownload,
    getCacheInfo,
    estimateDownloadSize,
    formatSize
  };
}
